/**
 * Phone notifications — the browser half.
 *
 * ONE FILE FOR BOTH SURFACES
 * --------------------------
 * The dashboard (office staff) and the driver portal ask the browser for
 * exactly the same things: is push possible here, may we, subscribe, stop.
 * Only the API they report to differs, so that is the only thing passed in
 * (`api`, an object with `getState` / `subscribe` / `unsubscribe`).
 *
 * WHY `supportState()` EXISTS
 * ---------------------------
 * The one rule that governs the UI on top of this: never show a switch that
 * would flip and silently do nothing. On iPhone, Web Push works ONLY for a
 * site added to the Home Screen (iOS 16.4+) — in Safari itself,
 * `window.PushManager` is not defined at all, so a driver who taps "turn on
 * notifications" in Safari would get nothing and no explanation. So instead
 * of a boolean, this returns a REASON, and each screen says something true
 * for it. The same applies to a permission the person has already denied:
 * the browser will not ask again, and pretending otherwise is a dead toggle.
 *
 * THE SERVICE WORKER
 * ------------------
 * `/push-sw.js`, registered at root scope, with no `fetch` handler — see the
 * long comment at the top of that file for why it cannot serve anyone a
 * stale app. `ensureWorker` also calls `registration.update()` every time,
 * so simply opening the app is an update check.
 */

const SW_URL = '/push-sw.js';

/** Why push is or is not available here. The UI says something true for each. */
export const PUSH_SUPPORT = Object.freeze({
  /** Everything is in place; we may ask. */
  READY: 'ready',
  /** Already on, on this device. */
  SUBSCRIBED: 'subscribed',
  /** iPhone/iPad in a browser: works only from the Home Screen icon. */
  IOS_NEEDS_HOME_SCREEN: 'ios-needs-home-screen',
  /** The person said no. The browser will not ask again; only they can undo it. */
  DENIED: 'denied',
  /** This browser has no Web Push at all (older Safari, some in-app views). */
  UNSUPPORTED: 'unsupported',
  /** The server has no VAPID keys yet, so nothing could be delivered. */
  NOT_CONFIGURED: 'not-configured',
});

const isIOS = (nav = window.navigator) => (
  /iPhone|iPad|iPod/.test(nav.userAgent || '')
  || (/Macintosh/.test(nav.userAgent || '') && Number(nav.maxTouchPoints) > 1)
);

const isStandalone = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
  } catch {
    return false;
  }
};

/** Everything Web Push needs, present in this browser. */
export const hasPushApis = () => (
  typeof window !== 'undefined'
  && 'serviceWorker' in window.navigator
  && 'PushManager' in window
  && 'Notification' in window
);

/**
 * What this device can do, before we touch the network.
 *
 * `publicKey` is the server's answer to "is push configured at all" — null
 * when the VAPID secret still holds its placeholder.
 */
export function supportState({ publicKey, subscribed } = {}) {
  if (subscribed) return PUSH_SUPPORT.SUBSCRIBED;

  if (!hasPushApis()) {
    // On an iPhone the APIs appear only inside the Home Screen app, so the
    // honest message is "add it to your Home Screen", not "unsupported".
    return isIOS() && !isStandalone()
      ? PUSH_SUPPORT.IOS_NEEDS_HOME_SCREEN
      : PUSH_SUPPORT.UNSUPPORTED;
  }

  if (window.Notification.permission === 'denied') return PUSH_SUPPORT.DENIED;
  if (publicKey === null || publicKey === undefined) return PUSH_SUPPORT.NOT_CONFIGURED;

  return PUSH_SUPPORT.READY;
}

/** base64url (what the API hands us) → the Uint8Array PushManager wants. */
export function urlBase64ToUint8Array(base64) {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const raw = window.atob(padded);

  return Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
}

/**
 * Register (or find) the push worker, and ask the browser to re-check it for
 * a newer copy. Resolves to null when service workers are unavailable.
 */
export async function ensureWorker() {
  if (!hasPushApis()) return null;

  const registration = await window.navigator.serviceWorker.register(SW_URL, { scope: '/' });

  // Opening the app IS the update check. Failure is fine — it only means we
  // keep the copy we have, which is a push-only worker either way.
  await registration.update().catch(() => undefined);

  return registration;
}

/** This browser's current subscription, or null. Never throws. */
export async function currentSubscription() {
  try {
    if (!hasPushApis()) return null;

    const registration = await window.navigator.serviceWorker.getRegistration(SW_URL);

    return registration ? await registration.pushManager.getSubscription() : null;
  } catch {
    return null;
  }
}

/** The shape the API stores: endpoint plus the two keys, nothing else. */
export const toApiSubscription = (subscription) => {
  const json = subscription.toJSON();

  return {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  };
};

/**
 * Turn notifications on for THIS device.
 *
 * MUST BE CALLED FROM A REAL USER GESTURE — a click or tap handler. Safari
 * (and, since Chrome 137, Chrome) refuses `Notification.requestPermission()`
 * outside one, and on iOS it is refused silently. Every caller in this app
 * is a button's onClick for that reason.
 *
 * @returns {Promise<{ ok: boolean, reason?: string, device?: object }>}
 */
export async function enablePush(api) {
  if (!hasPushApis()) {
    return { ok: false, reason: supportState({}) };
  }

  const { public_key: publicKey } = await api.getState();

  if (!publicKey) return { ok: false, reason: PUSH_SUPPORT.NOT_CONFIGURED };

  const permission = await window.Notification.requestPermission();

  if (permission !== 'granted') {
    return { ok: false, reason: permission === 'denied' ? PUSH_SUPPORT.DENIED : PUSH_SUPPORT.READY };
  }

  const registration = await ensureWorker();

  // Re-using an existing subscription keeps the same endpoint (and so the
  // same row server-side) instead of orphaning one per attempt.
  const subscription = await registration.pushManager.getSubscription()
    || await registration.pushManager.subscribe({
      // Required by every browser: a push that shows nothing is not allowed.
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

  const device = await api.subscribe(toApiSubscription(subscription));

  return { ok: true, device };
}

/**
 * The server's id for a subscription: SHA-256 of its endpoint, matching
 * `idForEndpoint` in PushSubscriptionRepository. Computed here so a device
 * can withdraw itself without the server having to hand out a mapping.
 */
export async function idOf(subscription) {
  const { endpoint } = subscription.toJSON();
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(endpoint));

  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Turn notifications off for THIS device: tell the API to forget it, then
 * drop the browser subscription.
 *
 * The API call goes first on purpose. If it fails we still hold a live
 * subscription and can try again; the other order could leave a row the
 * server would keep pushing to with no way for this browser to withdraw it.
 */
export async function disablePush(api) {
  const subscription = await currentSubscription();

  if (!subscription) return { ok: true };

  await api.unsubscribe(idOf(subscription));
  await subscription.unsubscribe().catch(() => undefined);

  return { ok: true };
}

/**
 * Keep the server's copy honest on app start.
 *
 * A browser can rotate or drop a subscription without telling anyone (a
 * `pushsubscriptionchange`, a reinstalled PWA, cleared site data). If this
 * device still has one, re-POST it: the server keys rows by endpoint, so a
 * re-POST of an unchanged subscription is a no-op, and a rotated one
 * replaces the dead row.
 *
 * Silent and best-effort — it runs on load and must never surface an error.
 */
export async function syncSubscription(api) {
  try {
    if (!hasPushApis() || window.Notification.permission !== 'granted') return;

    await ensureWorker();

    const subscription = await currentSubscription();

    if (subscription) await api.subscribe(toApiSubscription(subscription));
  } catch {
    // Offline, signed out, or push switched off server-side. Nothing here is
    // worth interrupting anyone for.
  }
}

export default {
  PUSH_SUPPORT,
  supportState,
  hasPushApis,
  ensureWorker,
  currentSubscription,
  enablePush,
  disablePush,
  syncSubscription,
  idOf,
  toApiSubscription,
  urlBase64ToUint8Array,
};
